// サーバーから渡されたチャート config の options を ApexCharts の設定に反映する(#1359)。
//
// ChartHelper#apex_chart_data は chart_options の中身を config.options の直下に平たく展開し、
// 手組みの config は config.options.chart_options に入れている。以前は JS が個別に拾うキー以外
// (legend / markers / tooltip / grid など)が黙って無視されていたため、両方の置き場所から
// ApexCharts の設定を取り出し、既定値に deep merge する。

// config.options のうち JS が個別に扱うキー(ApexCharts へそのまま渡さないもの)。
// - xaxis_type / xaxis_options / height / theme / keyword_breakdown / navigation / yaxis_label_formatter / mobile_primary_axis_only / mobile_hide_xaxis_annotation_labels / hide_overlapping_xaxis_annotation_labels / chart_options: JS 内部の指定
// - colors / stroke / annotations / dataLabels / export: JS が既定値との合成を個別に行う
// - chart / series / xaxis / yaxis: config 直下の同名キーから組み立てる(書式や軸の割り当てを通すため、ここからは受け付けない)
export const APEX_INTERNAL_OPTION_KEYS = Object.freeze([
  "xaxis_type",
  "xaxis_options",
  "height",
  "theme",
  "colors",
  "stroke",
  "annotations",
  "dataLabels",
  "export",
  "navigation",
  "keyword_breakdown",
  "yaxis_label_formatter",
  "mobile_primary_axis_only",
  "mobile_hide_xaxis_annotation_labels",
  "hide_overlapping_xaxis_annotation_labels",
  "chart_options",
  "chart",
  "series",
  "xaxis",
  "yaxis"
])

// tooltip の JS 側の目印。判定に使った後は ApexCharts に渡さない
// - tooltip.custom の文字列("story_datetime"): ApexCharts は関数しか受け付けない
// - tooltip.x.format の "datetime_with_time": ApexCharts の日付書式としては意味をなさない
const JS_SIDE_TOOLTIP_X_FORMAT_MARKERS = Object.freeze(["datetime_with_time"])

export function isPlainObject(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

// base に override を重ねた新しいオブジェクトを返す(どちらも書き換えない)。
// - プレーンなオブジェクト同士は再帰的に合成する(override に無いキーは base の値を残す)
// - 配列(colors など)やプリミティブは override で置き換える
// - override が undefined / null のキーは base を残す(Ruby の nil は JSON で null になるため)
// - base が関数(formatter / イベントハンドラ)で override が関数でない場合は base を残して console.warn する
//   (サーバーから JS 関数を文字列で渡しても関数としては動かず、既定の formatter を壊すだけのため)
export function deepMergeOptions(base, override, path = "") {
  if (override === undefined || override === null) return base
  if (typeof base === "function" && typeof override !== "function") {
    console.warn(
      `apex-chart: ${path || "(root)"} は関数で指定する必要があるため、渡された値(${typeof override})を無視して既定の関数を使います。`,
      { value: override }
    )
    return base
  }
  if (!isPlainObject(base) || !isPlainObject(override)) return override

  const merged = { ...base }
  Object.keys(override).forEach((key) => {
    merged[key] = deepMergeOptions(base[key], override[key], path ? `${path}.${key}` : key)
  })
  return merged
}

// options(config.options または config.options.chart_options)から ApexCharts にそのまま渡してよい設定だけを取り出す
export function extractPassthroughOptions(options) {
  if (!isPlainObject(options)) return {}

  const passthrough = {}
  Object.keys(options).forEach((key) => {
    if (APEX_INTERNAL_OPTION_KEYS.includes(key)) return
    if (options[key] === undefined || options[key] === null) return
    passthrough[key] = options[key]
  })

  if (isPlainObject(passthrough.tooltip)) {
    const tooltip = { ...passthrough.tooltip }
    if (typeof tooltip.custom === "string") delete tooltip.custom
    if (isPlainObject(tooltip.x) && JS_SIDE_TOOLTIP_X_FORMAT_MARKERS.includes(tooltip.x.format)) {
      const { format: _marker, ...x } = tooltip.x
      tooltip.x = x
    }
    passthrough.tooltip = tooltip
  }

  return passthrough
}

// ヘルパー経由(config.options 直下)と手組み(config.options.chart_options)の両方から設定を集める。
// 同じキーがあれば chart_options 側を優先する(差分・キーワード内訳・ストーリーのツールチップは JS が chart_options.tooltip に書き込むため)
export function buildPassthroughOptions(options) {
  if (!isPlainObject(options)) return {}

  return deepMergeOptions(
    extractPassthroughOptions(options),
    extractPassthroughOptions(options.chart_options)
  )
}

// JS が個別に扱うキー(colors / stroke など)の値を読む。
// ヘルパー経由(config.options 直下)と手組み(config.options.chart_options)の両方を見て、両方にあれば chart_options を優先する
// (ApexCharts に渡すほかのキーと同じ優先順)。どちらにも無ければ undefined
export function resolveInternalOption(options, key) {
  if (!isPlainObject(options)) return undefined

  const nested = isPlainObject(options.chart_options) ? options.chart_options[key] : undefined
  return nested !== undefined ? nested : options[key]
}

// tooltip の JS 側の目印(tooltip.x.format / tooltip.custom)を、どちらの置き場所に書かれていても読む
export function configuredTooltipOptions(options) {
  if (!isPlainObject(options)) return []

  return [options.chart_options?.tooltip, options.tooltip].filter(isPlainObject)
}

// 既定の凡例。chart_options.legend は applyPassthroughOptions で指定したキーだけ上書きされる。
// legendEvents: 系列の説明(description)用のイベント設定(apex_chart_controller.js#buildLegendTooltipEvents)
export function buildDefaultLegend(legendEvents = {}) {
  return {
    show: true,
    position: "bottom",
    horizontalAlign: "center",
    floating: false,
    itemMargin: { horizontal: 12, vertical: 6 },
    // 複数Y軸では ApexCharts が系列を軸ごとのグループにし、凡例を軸ごとに固めて並べ替える(既定 true)。
    // 凡例は常に系列定義の順に並べる(この設定は凡例の DOM 配置にしか効かない)
    clusterGroupedSeries: false,
    onItemHover: {
      highlightDataSeries: true
    },
    ...legendEvents
  }
}

const pad2 = (value) => value.toString().padStart(2, "0")

// 既定のツールチップ。
// - xaxisType: numeric / category が明示された軸では x を日付として解釈しない
// - showTime: true なら日時を常に YYYY/MM/DD HH:mm で出す(tooltip.x.format: "datetime_with_time")。
//   false なら 00:00 のときだけ日付のみ
// - formatSeriesValue: y の書式(lib/apex_chart_label_formatters.js の buildSeriesValueFormatter)
export function buildDefaultTooltip({ xaxisType, showTime = false, formatSeriesValue }) {
  return {
    theme: "light",
    shared: true,
    intersect: false,
    style: {
      fontSize: "12px",
      fontFamily: "inherit"
    },
    x: {
      formatter(value) {
        if (xaxisType && xaxisType !== "datetime") return value

        const date = new Date(value)
        if (isNaN(date.getTime())) return value

        // datetimeUTC: false の設定があるため、ローカルタイムで取得
        const ymd = `${date.getFullYear()}/${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}`
        const hour = pad2(date.getHours())
        const minute = pad2(date.getMinutes())
        if (showTime || hour !== "00" || minute !== "00") return `${ymd} ${hour}:${minute}`
        return ymd
      }
    },
    y: {
      formatter(value, context) {
        return formatSeriesValue(value, context)
      }
    }
  }
}

// chart_options に書いた ApexCharts の設定(legend / markers / tooltip / grid / plotOptions など)を、
// 組み立て終えた既定値(baseOptions)に deep merge して baseOptions を返す(baseOptions を書き換える)。
// yaxis は受け付けないキーなので、先に assignSeriesToYAxes で組み立てた軸の割り当てはそのまま残る
export function applyPassthroughOptions(baseOptions, options) {
  return Object.assign(baseOptions, deepMergeOptions(baseOptions, buildPassthroughOptions(options)))
}

// ---- datetime 軸の目盛り数(#1471) ----
// 呼び出し側が xaxis.tickAmount を書いていない datetime 軸(10 点以上)は、目盛り数をチャートの幅から決める。
// 以前は「点数 - 1」(1 日 1 目盛り)で、768px 以上でも 90 日表示などで日付ラベルが重なっていた。
// ApexCharts 7 は tickAmount を目安に暦の刻み(1日 / 2日 / 1週 / 1月…)を選ぶので、本数は前後する。

// 日付ラベル 1 個あたりに見込む幅(px)。チャート要素の幅をこれで割った数を目盛り数の上限にする
export const XAXIS_TICK_MIN_WIDTH_PX = 60

// datetime 軸の目盛り数。pointCount: 先頭の系列(series[0])の点数(apex_chart_controller.js#buildApexOptions が渡す)、chartWidth: チャート要素の幅(px)
// - 点数 - 1(1 日 1 目盛り)と「幅 ÷ 60px」の小さい方。最低 1
// - 幅が分からない(0 / 非表示のタブの中など)ときは従来どおり点数 - 1。表示されて幅が付いた時点で
//   apex_chart_controller.js#syncTickAmountToWidth が計算し直す
export function resolveDatetimeTickAmount({ pointCount, chartWidth, minWidthPerTick = XAXIS_TICK_MIN_WIDTH_PX } = {}) {
  const perPoint = Math.max(1, Math.floor(Number(pointCount) || 0) - 1)
  const width = Number(chartWidth)
  if (!Number.isFinite(width) || width <= 0) return perPoint

  return Math.max(1, Math.min(perPoint, Math.floor(width / minWidthPerTick)))
}

// ---- デスクトップ幅のデータラベルの間引き(#1471) ----
// apex_chart_controller.js#computeDisplayFlags は「前の表示との差が大きい / 局所的な山・谷 / 5 点おき / 最後の点」のどれかで
// 数値ラベルを出すため、日ごとに上下する系列ではほぼ全点に出る(TikTok 連携の 90 日で 1 系列約 90 個)。
// デスクトップ幅では、従来の間引きで隣り合うラベルの間隔(px)が 1 ラベルあたりの幅より狭いときだけ、
// 山・谷による追加をやめて等間隔に間引く(limitDataLabelFlags)。ズーム中は表示範囲内の点だけで同じ判定をする。
// スマホ幅(768px 未満)は buildMobileDataLabelsOptions が点の多い系列のラベルを消すので、ここは使わない。

// 1 ラベルあたりの幅(px)= 系列の最大値・最小値の表示文字数 × 6.5px + 8px を、24〜48px に収めたもの(estimateDataLabelWidth)。
// - データラベルは 10px 太字(buildSeriesValueFormatter の書式)。数字 1 文字は約 6px、カンマは約 3px なので、
//   1 文字 6.5px で見ればカンマ入りでも少し余る(「50,500」は実測約 39px、見積もり 6 × 6.5 = 39px)
// - 隣のラベルとの隙間として 8px を足す
// - 下限 24px: 「1」のような 1 桁でも、点の目印(marker)と左右のラベルが触れない幅
// - 上限 48px: 7 桁以上(「1,234,567」)でもこれ以上は空けない(間引きすぎて読める数値が減るのを防ぐ)
export const DATA_LABEL_CHAR_WIDTH_PX = 6.5
export const DATA_LABEL_GAP_PX = 8
export const DATA_LABEL_MIN_WIDTH_PX = 24
export const DATA_LABEL_MAX_WIDTH_PX = 48

// 描画前に、チャート要素の幅からプロット領域(w.globals.gridWidth)の幅を見積もる割合。
// Y 軸ラベル(複数軸なら左右)と右余白 24px の分を引く。768px 幅(要素 680px)の TikTok 連携で gridWidth は 541〜597px(0.80〜0.88)なので、
// 狭い側の 0.8 にする。描画後は apex_chart_controller.js#syncDataLabelsToView が実際の gridWidth で選び直す
export const DATA_LABEL_PLOT_WIDTH_RATIO = 0.8

export function estimatePlotWidth(chartWidth) {
  const width = Number(chartWidth)
  if (!Number.isFinite(width) || width <= 0) return 0
  return width * DATA_LABEL_PLOT_WIDTH_RATIO
}

// ラベルを出す候補の値か(従来の間引きと同じく、null と 0 には出さない)
const isDataLabelCandidate = (value) => typeof value === "number" && Number.isFinite(value) && Math.abs(value) > 1e-9

const defaultDataLabelFormatter = (value) => new Intl.NumberFormat("ja-JP").format(value)

// 系列の 1 ラベルあたりの幅(px)。values: 系列の数値、formatValue: データラベルの書式(既定は ja-JP の区切り)
// 最大値と最小値(負の値は符号の分だけ長い)を書式に通し、長い方の文字数で見積もる。ラベルを出す値が無ければ下限
export function estimateDataLabelWidth(values, formatValue = defaultDataLabelFormatter) {
  const candidates = (Array.isArray(values) ? values : []).filter(isDataLabelCandidate)
  if (candidates.length === 0) return DATA_LABEL_MIN_WIDTH_PX

  const length = Math.max(...[Math.max(...candidates), Math.min(...candidates)].map((value) => String(formatValue(value) ?? "").length))
  const width = length * DATA_LABEL_CHAR_WIDTH_PX + DATA_LABEL_GAP_PX
  return Math.min(DATA_LABEL_MAX_WIDTH_PX, Math.max(DATA_LABEL_MIN_WIDTH_PX, width))
}

// 等間隔に出すラベル数の上限(最低 1)。plotWidth: プロット領域の幅(px)、labelWidth: 1 ラベルあたりの幅(px)。幅が分からなければ null
export function resolveDataLabelMaxCount({ plotWidth, labelWidth = DATA_LABEL_MAX_WIDTH_PX } = {}) {
  const width = Number(plotWidth)
  if (!Number.isFinite(width) || width <= 0 || !(labelWidth > 0)) return null

  return Math.max(1, Math.floor(width / labelWidth))
}

// 間引く系列で、数値ラベルを出す点の旗(値と同じ長さの true / false の配列)を返す。
// values: 系列の数値(null 可)、maxCount: resolveDataLabelMaxCount の上限、minStep: ラベル同士に空ける点の数(既定 1)
// - 系列全体の最後の点・最大値・最小値は必ず出す(同じ値が複数あれば最初の点。上限が 3 未満ならこの順で上限まで)
//   最大値・最小値が最後の点と同じ値なら最後の点にまとめる(同じ数値が 2 つ並ばないように)。
//   最大値・最小値が、先に決めた点(最後の点 → 最大値の順)から minStep 未満の位置にあるときは、重なるので出さない
// - 残りの枠(上限 - 上の点の数)で等間隔に出す。間隔は「値のある区間(最初の候補点〜最後の候補点)の点数 ÷ 残りの枠」の切り上げと
//   minStep の大きい方なので、合計は上限を超えず、隣り合うラベルは minStep 以上離れる。
//   前半が 0 の系列(途中で連携したアカウントなど)でも、0 の区間の分だけ枠を無駄にしない(候補点 = null と 0 以外の点)
// - 等間隔の点のうち、必ず出す点との距離が間隔未満のものは出さない(数値が重なるため)
export function selectCappedDataLabelFlags(values, { maxCount, minStep = 1 } = {}) {
  const list = Array.isArray(values) ? values : []
  const flags = new Array(list.length).fill(false)
  const limit = Math.floor(Number(maxCount))
  if (!Number.isFinite(limit) || limit < 1) return flags
  const minimumStep = Math.max(1, Math.ceil(Number(minStep) || 1))

  const candidates = []
  list.forEach((value, index) => {
    if (isDataLabelCandidate(value)) candidates.push(index)
  })
  if (candidates.length === 0) return flags

  let maxIndex = candidates[0]
  let minIndex = candidates[0]
  candidates.forEach((index) => {
    if (list[index] > list[maxIndex]) maxIndex = index
    if (list[index] < list[minIndex]) minIndex = index
  })
  const lastIndex = candidates[candidates.length - 1]
  if (list[maxIndex] === list[lastIndex]) maxIndex = lastIndex
  if (list[minIndex] === list[lastIndex]) minIndex = lastIndex
  const required = []
  ;[lastIndex, maxIndex, minIndex].forEach((index) => {
    if (required.length >= limit || required.includes(index)) return
    if (required.some((chosen) => Math.abs(index - chosen) < minimumStep)) return
    required.push(index)
  })
  required.forEach((index) => { flags[index] = true })

  const budget = limit - required.length
  if (budget <= 0) return flags

  const spanLength = lastIndex - candidates[0] + 1
  const step = Math.max(minimumStep, Math.ceil(spanLength / budget))
  let lastGridIndex = null
  candidates.forEach((index) => {
    if (lastGridIndex !== null && index - lastGridIndex < step) return
    // 必ず出す点の近くで出さなかった場合も、等間隔の位置はずらさない
    lastGridIndex = index
    if (required.some((requiredIndex) => Math.abs(index - requiredIndex) < step)) return
    flags[index] = true
  })

  return flags
}

// 旗の立っている点のうち、隣り合う 2 点の番号の差の最小値(2 点未満なら Infinity)
export function minDataLabelIndexGap(flags) {
  let previous = null
  let gap = Infinity
  ;(Array.isArray(flags) ? flags : []).forEach((flag, index) => {
    if (!flag) return
    if (previous !== null) gap = Math.min(gap, index - previous)
    previous = index
  })
  return gap
}

// 従来の間引きの旗(baseFlags。apex_chart_controller.js#computeDisplayFlags)を、プロット領域の幅に合わせる。
// values: 表示範囲の系列の数値、plotWidth: プロット領域の幅(px。描画後は w.globals.gridWidth)、labelWidth: 1 ラベルあたりの幅(px)
// - 1 点あたりの px = plotWidth ÷ (点数 - 1)
// - 従来の旗で隣り合うラベルの最小間隔(番号の差 × 1 点あたりの px)が labelWidth 以上ならそのまま(重なっていない山・谷は消さない)
// - 狭ければ selectCappedDataLabelFlags で等間隔にする。上限は floor(plotWidth ÷ labelWidth) と従来の旗の数の小さい方
//   (隣り合う 1 組だけが狭いときに、系列全体を埋め直してラベルが増えないように)、ラベル同士は ceil(labelWidth ÷ 1 点あたりの px) 点以上空ける
// - 幅が分からない(0)、点が 2 点未満のときはそのまま
export function limitDataLabelFlags(values, baseFlags, { plotWidth, labelWidth = DATA_LABEL_MAX_WIDTH_PX } = {}) {
  const flags = Array.isArray(baseFlags) ? baseFlags : []
  const pointCount = Array.isArray(values) ? values.length : 0
  const widthMaxCount = resolveDataLabelMaxCount({ plotWidth, labelWidth })
  if (!widthMaxCount || pointCount < 2) return flags

  const pxPerPoint = plotWidth / (pointCount - 1)
  if (minDataLabelIndexGap(flags) * pxPerPoint >= labelWidth) return flags

  const maxCount = Math.min(widthMaxCount, flags.filter(Boolean).length)
  return selectCappedDataLabelFlags(values, { maxCount, minStep: Math.ceil(labelWidth / pxPerPoint) })
}

// 表示範囲(ズーム中の xaxis.min / max)に入る点の番号の範囲 { start, end }(両端を含む)。
// xValues: 系列の各点の x(datetime / numeric 軸のミリ秒・数値)、xaxisType: w.config.xaxis.type。
// - 範囲が無い、軸が datetime / numeric でない(category 軸。ApexCharts はズーム範囲を点の番号で返すので x と比べられない)、
//   x が数値でない点がある、のどれかなら全体
// - 範囲に入る点が無ければ { start: 0, end: -1 }
const ZOOM_RANGE_AXIS_TYPES = Object.freeze(["datetime", "numeric"])
export function resolveVisibleIndexRange(xValues, { min, max, xaxisType } = {}) {
  const list = Array.isArray(xValues) ? xValues : []
  const full = { start: 0, end: list.length - 1 }
  const hasMin = Number.isFinite(min)
  const hasMax = Number.isFinite(max)
  if ((!hasMin && !hasMax) || !ZOOM_RANGE_AXIS_TYPES.includes(xaxisType) || !list.every(Number.isFinite)) return full

  let start = -1
  let end = -1
  list.forEach((x, index) => {
    if ((hasMin && x < min) || (hasMax && x > max)) return
    if (start < 0) start = index
    end = index
  })
  return start < 0 ? { start: 0, end: -1 } : { start, end }
}

// 呼び出し側の dataLabels で間引きを指定しているか(指定していればデスクトップ幅の間引きを付けない)。
// customOptions: config.options.chart_options.dataLabels ?? config.options.dataLabels
// - false / enabled の明示 / 間引き設定(minSpacing / relativeThreshold / absoluteThreshold / minAbsolute)のどれかがあれば true
// - enabledOnSeries や style だけの指定は対象外(間引く)
const DATA_LABEL_SAMPLING_KEYS = Object.freeze(["minSpacing", "relativeThreshold", "absoluteThreshold", "minAbsolute"])
export function usesCustomDataLabelSampling(customOptions) {
  if (customOptions === false) return true
  if (!isPlainObject(customOptions)) return false
  if (customOptions.enabled !== undefined && customOptions.enabled !== null) return true
  return DATA_LABEL_SAMPLING_KEYS.some((key) => customOptions[key] !== undefined && customOptions[key] !== null)
}

// ---- スマホ幅(768px 未満)の設定(#1371 / #1486) ----
// 375〜768px では x 軸の日付ラベルやデータラベルが重なるため、スマホ幅でだけ下の設定を既定値に重ねる(buildMobileOverrideOptions)。
// 以前は ApexCharts の responsive に入れていたが、幅を広げても元に戻らなかった(#1486)。
// いまは apex_chart_controller.js が描画時の幅で「既定値だけ」か「既定値 + スマホ幅の設定」かを選び、
// 幅が 768px をまたいだらチャートを作り直す(lib/apex_chart_viewport.js)。
// 重ねるときは deepMergeOptions を使うので、ここで返さないキー(xaxis.labels.formatter / rotate など)は既定値のまま残る。

// スマホ幅での datetime 軸の目盛り数の上限(ラベルは上限 + 1 個)。
// 375px 幅(チャート幅 311px)・9px・-15 度回転で実測し、7〜180 日のどの期間でもラベル間に 10px 以上の隙間が残る数として 6 にしている
// (8 だと 180 日で隙間がほぼ 0px。上限を付けない従来設定は 12 日以上で重なっていた)
export const MOBILE_XAXIS_MAX_TICK_AMOUNT = 6

// この点数以上の折れ線・面グラフの系列は、スマホ幅でデータラベルを出さない(点の間隔が狭く数値が重なるため)
export const MOBILE_DATA_LABELS_HIDE_MIN_POINTS = 10

// この点数以上の棒グラフの系列も、スマホ幅でデータラベルを出さない(#1471)。
// 棒の数値は 5 点おき(minSpacing: 5)に間引いているが、前後との差が大きい点や山・谷は間隔に関係なく出るため、
// 点が多いと重なる(375px・90 日の混合チャートで 84 個中 21 件)。1 か月(31 日)までは従来どおり残す
export const MOBILE_BAR_DATA_LABELS_HIDE_MIN_POINTS = 32

const LINE_LIKE_CHART_TYPES = Object.freeze(["line", "area"])

const seriesPointCount = (series) => (Array.isArray(series?.data) ? series.data.length : 0)

// スマホ幅の xaxis。config はサーバーから渡されたチャート config(apex_chart_controller.js の configValue)
// - 目盛りを自動で決める datetime 軸(呼び出し側が xaxis.tickAmount を明示していない)だけ、目盛り数を上限に抑える。
//   点数が上限以下なら何もしない
// - 重なるラベルを隠す(hideOverlappingLabels)。呼び出し側が明示していればそれに従う
// - ヒートマップ(x 軸は時間帯などのカテゴリ)と横棒(x 軸は値)は、デスクトップの回転設定と同じく対象外
export function buildMobileXaxisOptions(config, { maxTickAmount = MOBILE_XAXIS_MAX_TICK_AMOUNT } = {}) {
  const xaxis = { labels: { style: { fontSize: "9px" } } }

  const chartType = config?.chart?.type
  const isHorizontalBar = chartType === "bar" && Boolean(config?.plotOptions?.bar?.horizontal)
  if (chartType === "heatmap" || isHorizontalBar) return xaxis

  if (config?.xaxis?.labels?.hideOverlappingLabels === undefined) {
    xaxis.labels.hideOverlappingLabels = true
  }

  const xaxisType = config?.xaxis?.type || config?.options?.xaxis_type
  if (xaxisType === "datetime" && config?.xaxis?.tickAmount === undefined) {
    const series = Array.isArray(config?.series) ? config.series : []
    const maxPoints = Math.max(0, ...series.map(seriesPointCount))
    if (maxPoints - 1 > maxTickAmount) {
      xaxis.tickAmount = maxTickAmount
    }
  }

  return xaxis
}

// スマホ幅の dataLabels。
// - 折れ線・面グラフの系列(混合チャートの line / area 系列を含む)で点が多いもの(10 点以上)は、データラベルを出さない
// - 棒グラフの系列も、点がとても多いもの(32 点以上)は出さない(#1471)。横棒は対象外(数値は棒の中に出る)
// - それ以外の系列は残す(残る系列があれば enabledOnSeries で絞り、無ければ enabled: false)
// - 呼び出し側が dataLabels を false にしている / enabled を明示している場合はそれに従う(フォントだけ小さくする)
// - 呼び出し側の enabledOnSeries は、その中から上の系列を除く
export function buildMobileDataLabelsOptions(config, {
  minPoints = MOBILE_DATA_LABELS_HIDE_MIN_POINTS,
  minBarPoints = MOBILE_BAR_DATA_LABELS_HIDE_MIN_POINTS
} = {}) {
  const dataLabels = { style: { fontSize: "8px" } }

  const chartType = config?.chart?.type
  if (chartType === "heatmap") return dataLabels

  const customOptions = config?.options?.chart_options?.dataLabels ?? config?.options?.dataLabels
  if (customOptions === false) return dataLabels
  if (isPlainObject(customOptions) && customOptions.enabled !== undefined) return dataLabels

  const isHorizontalBar = chartType === "bar" && Boolean(config?.plotOptions?.bar?.horizontal)
  const hideMinPoints = (item) => {
    if (LINE_LIKE_CHART_TYPES.includes(item?.type || chartType)) return minPoints
    return isHorizontalBar ? Infinity : minBarPoints
  }

  const series = Array.isArray(config?.series) ? config.series : []
  const crowdedIndexes = series
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => seriesPointCount(item) >= hideMinPoints(item))
    .map(({ index }) => index)
  if (crowdedIndexes.length === 0) return dataLabels

  const enabledIndexes = Array.isArray(customOptions?.enabledOnSeries)
    ? customOptions.enabledOnSeries
    : series.map((_item, index) => index)
  const remainingIndexes = enabledIndexes.filter((index) => !crowdedIndexes.includes(index))
  if (remainingIndexes.length === 0) return { ...dataLabels, enabled: false }

  return { ...dataLabels, enabledOnSeries: remainingIndexes }
}

// スマホ幅の yaxis。yaxis は apex_chart_controller.js#buildApexOptions で組み立て済みの軸(formatter / seriesName 入り)。
// 既定は従来どおり、既存の軸をまるごと複製してラベルのフォントだけ小さくする。
// options.mobile_primary_axis_only が true のチャート(複数Y軸)は、2本目以降の軸を隠し、全軸のタイトルを空にする。
// 軸3本とタイトルで描画幅が潰れるのを防ぐため。隠した軸の系列も目盛りの計算には使われ、値はツールチップで読める
export function buildMobileYaxisOptions(yaxis, config) {
  const shrinkLabels = (axis) => ({ ...axis, labels: { ...axis?.labels, style: { fontSize: "9px" } } })
  if (!Array.isArray(yaxis)) return shrinkLabels(yaxis)

  if (resolveInternalOption(config?.options, "mobile_primary_axis_only") !== true) return yaxis.map(shrinkLabels)

  return yaxis.map((axis, index) => ({
    ...shrinkLabels(axis),
    ...(index >= 1 ? { show: false } : {}),
    title: { ...axis?.title, text: "" }
  }))
}

function buildMobileXaxisAnnotationOptions(config) {
  const annotations = config?.options?.annotations || config?.options?.chart_options?.annotations
  if (!isPlainObject(annotations) || !Array.isArray(annotations.xaxis)) return {}

  return {
    annotations: {
      ...annotations,
      xaxis: annotations.xaxis.map((annotation) => {
        if (!isPlainObject(annotation?.label)) return annotation

        return {
          ...annotation,
          label: {
            ...annotation.label,
            text: "",
            style: { ...annotation.label.style, background: "transparent" }
          }
        }
      })
    }
  }
}

// スマホ幅で既定値に重ねる設定。yaxis は apex_chart_controller.js#buildApexOptions で組み立て済みの軸
export function buildMobileOverrideOptions(config, yaxis) {
  return {
    chart: {
      toolbar: { show: false }
    },
    // スマホ幅はツールバーを隠すので、ツールバーとの衝突を避ける上余白を戻す
    grid: {
      padding: { top: 0 }
    },
    // 点の多い系列はデータラベルを出さない(フォントは小さく)
    dataLabels: buildMobileDataLabelsOptions(config),
    legend: {
      fontSize: "10px",
      itemMargin: { horizontal: 6, vertical: 3 }
    },
    // datetime 軸の目盛り数を抑え、重なるラベルを隠す(フォントは小さく)。xaxis.labels の formatter / rotate などは残る
    xaxis: buildMobileXaxisOptions(config),
    // ラベルのフォントを小さくする。options.mobile_primary_axis_only のチャートは左軸だけ残す
    yaxis: buildMobileYaxisOptions(yaxis, config),
    ...(resolveInternalOption(config?.options, "mobile_hide_xaxis_annotation_labels") === true
      ? buildMobileXaxisAnnotationOptions(config)
      : {})
  }
}

// 組み立て終えた既定値(baseOptions)から、描画する幅の設定を返す(baseOptions は書き換えない)。
// - mobile が false なら baseOptions をそのまま返す
// - mobile が true なら buildMobileOverrideOptions を deep merge した新しいオブジェクトを返す
// - 呼び出し側が responsive を渡していれば、ApexCharts の responsive に任せる(何も重ねない)
// 幅が 768px をまたいだら、apex_chart_controller.js はこの関数を通して作り直す。既定値は書き換えないので、
// スマホ幅 → デスクトップ幅に戻すとツールバー・フォント・データラベル・軸がすべて元の設定になる
export function applyViewportOptions(baseOptions, config, { mobile = false } = {}) {
  if (!mobile || usesCustomResponsive(baseOptions)) return baseOptions

  return deepMergeOptions(baseOptions, buildMobileOverrideOptions(config, baseOptions.yaxis))
}

// 呼び出し側が chart_options.responsive を渡しているか(渡していればスマホ幅の既定は付けない。空配列も「渡した」扱い)
export function usesCustomResponsive(baseOptions) {
  return Boolean(baseOptions?.responsive)
}
